import { DEFAULT_WINDOW_MINUTES } from "components/manualReview/hooks/useManualReviewNavigation";
import {
  calculateSnappedMaxStart,
  FIVE_MINUTES_IN_SECONDS,
  SECONDS_IN_DAY,
  snapToInterval,
} from "components/manualReview/utils/timeUtils";
import { Dayjs } from "dayjs";
import { RefObject, useCallback, useMemo, useState } from "react";

interface UseTimelineScrubArgs {
  plotAreaRef: RefObject<HTMLElement | null>;
  dayStart: Dayjs;
  dayEnd: Dayjs;
  onWindowChange: (newStart: Dayjs) => void;
}

export const useTimelineScrub = ({
  plotAreaRef,
  dayStart,
  dayEnd,
  onWindowChange,
}: UseTimelineScrubArgs) => {
  const [isScrubbing, setIsScrubbing] = useState(false);

  const maxStart = useMemo(
    () => calculateSnappedMaxStart(dayStart, dayEnd, DEFAULT_WINDOW_MINUTES),
    [dayStart, dayEnd]
  );

  const calculateClampedStart = useCallback(
    (clientX: number): Dayjs | null => {
      const plotArea = plotAreaRef.current;
      if (!plotArea) return null;

      const rect = plotArea.getBoundingClientRect();
      const positionPercent = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width)
      );
      const clickedTime = dayStart.add(
        Math.floor(positionPercent * SECONDS_IN_DAY),
        "second"
      );
      const newStart = snapToInterval(
        clickedTime,
        dayStart,
        FIVE_MINUTES_IN_SECONDS,
        "round"
      );

      return newStart.isAfter(maxStart) ? maxStart : newStart;
    },
    [plotAreaRef, dayStart, maxStart]
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setIsScrubbing(true);
      const clampedStart = calculateClampedStart(event.clientX);
      if (clampedStart) {
        onWindowChange(clampedStart);
      }
    },
    [calculateClampedStart, onWindowChange]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!isScrubbing) return;
      const clampedStart = calculateClampedStart(event.clientX);
      if (clampedStart) {
        onWindowChange(clampedStart);
      }
    },
    [isScrubbing, calculateClampedStart, onWindowChange]
  );

  const stopScrubbing = useCallback(() => {
    setIsScrubbing(false);
  }, []);

  return {
    isScrubbing,
    calculateClampedStart,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp: stopScrubbing,
    handleMouseLeave: stopScrubbing,
  };
};
